import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiChevronLeft, FiChevronRight, FiChevronDown, FiSearch, FiUsers, FiAlertTriangle, FiCalendar, FiLayers } from "react-icons/fi";
import { toISO } from "./dashboardStats";
import {
  fetchActiveClients, fetchClientUtilization, fetchProjectUtilization, fetchFpsUtilization, isFps,
  periodRange, shiftAnchor, periodLabel, buildBuckets, bucketKeyFor,
} from "./Utilizationapi";

// Series colors (validated: lightness, chroma, CVD and contrast checks pass on white)
const BILL = "#1D5FD1";
const NONBILL = "#2A9D8F";
const OVER = "#C2410C"; // status: over capacity (always shown with text + icon)
const FPS = "#7C3AED";  // FPS projects (always shown with the "FPS" label too)
const projIsFps = (p) => (p && p.fps != null ? !!p.fps : isFps(p && p.project_name));
const FpsChip = () => (
  <span className="inline-flex items-center h-[18px] px-1.5 rounded-full text-[10px] font-semibold bg-[#F1ECFB] text-[#5B33A6] shrink-0">FPS</span>
);

const MODES = [
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
  { id: "year", label: "Year" },
];

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 1 });
const parseISO = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || "");
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};

// ── Small pieces ─────────────────────────────────────────────────────
const Card = ({ className = "", children, ...rest }) => (
  <div className={`bg-white border border-[#E6E9EF] rounded-[14px] ${className}`} {...rest}>{children}</div>
);

const Swatch = ({ color }) => (
  <span aria-hidden="true" className="inline-block w-[10px] h-[10px] rounded-[3px] shrink-0" style={{ background: color }} />
);

const Kpi = ({ label, value, unit, sub, swatch, onClick, active, activeColor = "#1D5FD1" }) => (
  <Card className={`p-4 flex flex-col gap-1 ${onClick ? "cursor-pointer hover:border-[#BFD3F6] hover:shadow-sm" : ""}`}
    style={active ? { borderColor: activeColor, boxShadow: `0 0 0 1px ${activeColor}` } : undefined}
    {...(onClick ? { role: "button", tabIndex: 0, onClick, onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } } : {})}>
    <span className="text-[12px] text-[#5B6474] flex items-center gap-2">{swatch && <Swatch color={swatch} />}{label}</span>
    <span className="text-[24px] font-semibold leading-tight">
      {value}{unit && <span className="text-[14px] text-[#5B6474] font-medium"> {unit}</span>}
    </span>
    {sub && <span className="text-[11px] text-[#5B6474]">{sub}</span>}
  </Card>
);

const Skeleton = ({ h = 16, className = "" }) => (
  <div className={`bg-[#F1F3F7] rounded-md animate-pulse ${className}`} style={{ height: h }} />
);

// Stacked billable / non-billable columns with a hover tooltip
function HoursChart({ buckets, values }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...buckets.map((b) => (values[b.key]?.b || 0) + (values[b.key]?.n || 0)));
  const PX = 160;
  const dense = buckets.length > 14; // month view: label every few days

  return (
    <div className="relative">
      <div className="overflow-x-auto">
        <div
          role="img"
          aria-label="Hours per period, billable and non-billable"
          className="grid items-end gap-[2px] border-b border-[#E6E9EF] px-1"
          style={{ gridTemplateColumns: `repeat(${buckets.length}, minmax(${dense ? 14 : 28}px, 1fr))`, height: PX + 24, minWidth: dense ? 520 : 0 }}
        >
          {buckets.map((b, i) => {
            const v = values[b.key] || { b: 0, n: 0 };
            const tot = v.b + v.n;
            return (
              <div
                key={b.key}
                className="h-full flex flex-col justify-end items-center cursor-default"
                onMouseEnter={() => setHover({ i, b, v })}
                onMouseLeave={() => setHover(null)}
                style={{ background: hover?.i === i ? "#F4F6FA" : "transparent" }}
              >
                <div className="w-full max-w-[36px] flex flex-col gap-[2px]">
                  {v.n > 0 && <div style={{ height: (v.n / max) * PX, background: NONBILL, borderRadius: v.b > 0 ? "4px 4px 0 0" : "4px 4px 0 0" }} />}
                  {v.b > 0 && <div style={{ height: (v.b / max) * PX, background: BILL, borderRadius: v.n > 0 ? 0 : "4px 4px 0 0" }} />}
                  {tot === 0 && <div style={{ height: 2, background: b.weekend ? "transparent" : "#E6E9EF" }} />}
                </div>
              </div>
            );
          })}
        </div>
        <div
          className="grid gap-[2px] px-1 pt-1.5"
          style={{ gridTemplateColumns: `repeat(${buckets.length}, minmax(${dense ? 14 : 28}px, 1fr))`, minWidth: dense ? 520 : 0 }}
        >
          {buckets.map((b, i) => (
            <span key={b.key} className={`text-[10px] text-center ${b.weekend ? "text-[#A0A7B4]" : "text-[#5B6474]"}`}>
              {!dense || i === 0 || (i + 1) % 5 === 0 ? b.label : ""}
            </span>
          ))}
        </div>
      </div>

      {hover && (
        <div
          role="tooltip"
          className="absolute top-0 pointer-events-none bg-white border border-[#E6E9EF] rounded-[10px] shadow-md px-3 py-2 text-[12px] z-10"
          style={{ left: `clamp(0px, calc(${((hover.i + 0.5) / buckets.length) * 100}% - 70px), calc(100% - 150px))`, width: 150 }}
        >
          <div className="font-semibold mb-1">{hover.b.full || hover.b.label}</div>
          <div className="flex justify-between gap-2"><span className="flex items-center gap-1.5"><Swatch color={BILL} />Billable</span><span>{fmt(hover.v.b)} h</span></div>
          <div className="flex justify-between gap-2"><span className="flex items-center gap-1.5"><Swatch color={NONBILL} />Non-billable</span><span>{fmt(hover.v.n)} h</span></div>
          <div className="flex justify-between gap-2 border-t border-[#EEF0F4] mt-1 pt-1 font-semibold"><span>Total</span><span>{fmt(hover.v.b + hover.v.n)} h</span></div>
        </div>
      )}
    </div>
  );
}

// ── Task with its comments (hours per comment) ───────────────────────
const STATUS_STYLE = {
  Approved: "bg-[#E3F4EE] text-[#1E6B52]",
  Denied: "bg-[#FDE6D6] text-[#9A3412]",
  Pending: "bg-[#F1F3F7] text-[#3D4555]",
};

function TaskBlock({ t }) {
  const [all, setAll] = useState(false);
  const LIMIT = 5;
  const shown = all ? t.comments : t.comments.slice(0, LIMIT);
  const diff = Math.abs((t.comment_hours || 0) - (t.hours || 0)) >= 0.25;
  return (
    <li className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-3 text-[13px]">
        <span className="flex items-center gap-2 min-w-0">
          <Swatch color={t.billable ? BILL : NONBILL} />
          <span className="truncate font-medium">{t.item_name || "Untitled task"}</span>
          <span className="text-[11px] text-[#5B6474] shrink-0">{t.billable ? "Billable" : "Non-billable"}</span>
        </span>
        <span className="font-semibold shrink-0">{fmt(t.hours)} h</span>
      </div>
      {t.comments.length > 0 ? (
        <ul className="m-0 p-0 list-none flex flex-col gap-1 pl-[18px]">
          {shown.map((c) => (
            <li key={c.comment_id} className="grid grid-cols-[82px_52px_minmax(0,1fr)_auto] items-start gap-2 text-[12px] py-1 border-b border-dashed border-[#E6E9EF] last:border-0">
              <span className="text-[#5B6474]">{parseISO(c.date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}</span>
              <span className="font-semibold">{fmt(c.hours)} h</span>
              <span className="text-[#3D4555] break-words">{c.text || <i className="text-[#8A93A3]">No comment text</i>}</span>
              <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${STATUS_STYLE[c.status] || STATUS_STYLE.Pending}`}>{c.status}</span>
            </li>
          ))}
          <li className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-[#5B6474] pt-1">
            <span>
              Comments total <b className="text-[#0F172A]">{fmt(t.comment_hours)} h</b>
              {diff && <span className="text-[#9A3412]"> · differs from timesheet {fmt(t.hours)} h</span>}
            </span>
            {t.comments.length > LIMIT && (
              <button type="button" onClick={() => setAll((x) => !x)}
                className="border-0 bg-transparent p-0 text-[12px] font-semibold text-[#1D5FD1] cursor-pointer">
                {all ? "Show fewer" : `Show all ${t.comments.length} comments`}
              </button>
            )}
          </li>
        </ul>
      ) : (
        <span className="pl-[18px] text-[11px] text-[#8A93A3]">No comments logged for this task in this period</span>
      )}
    </li>
  );
}

// ── Detail view (client or project — same data shape) ────────────────
// bill / setBill: "all" | "billable" | "nonbillable" (kept in the URL as ?bill=, so the dashboard can link to it)
function Detail({ data, mode, range, onOpenProject, onPrevPeriod, onYearView, bill = "all", setBill = () => {} }) {
  const [expanded, setExpanded] = useState(null);
  const [focusEmp, setFocusEmp] = useState(null);
  const [projFilter, setProjFilter] = useState("all"); // all | normal | fps
  // Every card is clickable: it filters (billable / non-billable / FPS) and scrolls down to the matching section
  const chartRef = useRef(null), projRef = useRef(null), taskRef = useRef(null), peopleRef = useRef(null);
  const goTo = (ref) => setTimeout(() => ref.current && ref.current.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  const billLabel = bill === "billable" ? "Billable" : bill === "nonbillable" ? "Non-billable" : "";
  const projBillOk = (p) => bill === "all" || (bill === "billable" ? Number(p.billable_hours) > 0 : Number(p.non_billable_hours) > 0);
  const taskBillOk = (t) => bill === "all" || (bill === "billable" ? !!t.billable : !t.billable);
  const isFpsScope = data.scope.type === "fps";
  const isClient = data.scope.type === "client" || isFpsScope;   // list of projects + people
  const allEmps = data.employees || [];
  const projects = data.projects || [];

  // FPS vs normal split (from the project rows)
  const fpsProjects = projects.filter(projIsFps), normalProjects = projects.filter((p) => !projIsFps(p));
  const hasFps = !isFpsScope && fpsProjects.length > 0;
  const sumOf = (list, k) => list.reduce((t, p) => t + (Number(p[k]) || 0), 0);
  const split = {
    fps: { h: sumOf(fpsProjects, "hours"), b: sumOf(fpsProjects, "billable_hours"), n: sumOf(fpsProjects, "non_billable_hours") },
    normal: { h: sumOf(normalProjects, "hours"), b: sumOf(normalProjects, "billable_hours"), n: sumOf(normalProjects, "non_billable_hours") },
  };
  const empFps = (e) => (e.projects || []).filter(projIsFps).reduce((t, p) => t + (Number(p.hours) || 0), 0);
  const emps = (!hasFps || projFilter === "all" ? allEmps
    : allEmps.filter((e) => (projFilter === "fps" ? empFps(e) > 0 : e.hours - empFps(e) > 0)))
    .filter((e) => bill === "all" || (bill === "billable" ? e.billable_hours > 0 : e.non_billable_hours > 0));

  // Tasks across everyone (one row per task): shown when Billable / Non-billable is picked
  const taskRows = useMemo(() => {
    const map = new Map();
    allEmps.forEach((e) => (e.projects || []).forEach((p) => {
      if (hasFps && projFilter !== "all" && (projFilter === "fps") !== projIsFps(p)) return;
      (p.tasks || []).forEach((t) => {
        if (!taskBillOk(t)) return;
        const k = `${p.project_id}|${t.item_id}`;
        const row = map.get(k) || { key: k, item_name: t.item_name, project_id: p.project_id, project_name: p.project_name, client_name: p.client_name,
          fps: projIsFps(p), billable: !!t.billable, hours: 0, people: [] };
        row.hours += Number(t.hours) || 0;
        row.people.push({ name: e.employee_name, hours: Number(t.hours) || 0 });
        map.set(k, row);
      });
    }));
    return [...map.values()].sort((a, b) => b.hours - a.hours);
  }, [allEmps, bill, projFilter, hasFps]); // eslint-disable-line react-hooks/exhaustive-deps
  const fpsPeople = allEmps.filter((e) => empFps(e) > 0).length;

  const buckets = useMemo(() => buildBuckets(mode, range), [mode, range]);
  const chartValues = useMemo(() => {
    const out = {};
    allEmps.filter((e) => focusEmp == null || e.employee_id === focusEmp).forEach((e) =>
      (e.daily || []).forEach((d) => {
        const k = bucketKeyFor(mode, d.date);
        out[k] = out[k] || { b: 0, n: 0 };
        out[k].b += Number(d.billable_hours) || 0;
        out[k].n += Number(d.non_billable_hours) || 0;
      }));
    return out;
  }, [allEmps, focusEmp, mode]);

  const totals = allEmps.reduce((t, e) => ({ h: t.h + e.hours, b: t.b + e.billable_hours, n: t.n + e.non_billable_hours, logged: t.logged + (e.hours > 0 ? 1 : 0) }), { h: 0, b: 0, n: 0, logged: 0 });
  const loggedEmps = allEmps.filter((e) => e.hours > 0);
  const avgUtil = loggedEmps.length ? loggedEmps.reduce((s, e) => s + e.utilization_pct, 0) / loggedEmps.length : 0;
  const maxEmpHours = Math.max(1, ...emps.map((e) => e.hours));
  const maxProjHours = Math.max(1, ...projects.map((p) => p.hours));
  const projTotal = projects.reduce((t, p) => t + (Number(p.hours) || 0), 0);
  const scopeLabel = isFpsScope ? "FPS projects" : data.scope.type === "client" ? data.scope.client_name : data.scope.project_name;
  const hasData = totals.h > 0;

  return (
    <>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-col">
          <h2 className="m-0 text-[20px] font-semibold">{scopeLabel}</h2>
          <span className="text-[13px] text-[#5B6474]">
            {isFpsScope
              ? `All clients · ${projects.length} FPS project${projects.length === 1 ? "" : "s"} in ${data.scope.clients_count || 0} client${data.scope.clients_count === 1 ? "" : "s"}`
              : isClient ? `Client · ${projects.length} active project${projects.length === 1 ? "" : "s"}${hasFps ? ` (${fpsProjects.length} FPS)` : ""}`
              : [data.scope.client_name, data.scope.chargeability, isFps(data.scope.project_name) ? "FPS project" : ""].filter(Boolean).join(" · ")} · {periodLabel(mode, range)}
          </span>
        </div>
      </div>

      {/* No hours in this period */}
      {!hasData && (
        <Card className="p-8 flex flex-col items-center text-center gap-2">
          <span aria-hidden="true" className="w-11 h-11 rounded-full bg-[#F1F3F7] text-[#5B6474] flex items-center justify-center text-[20px]"><FiCalendar /></span>
          <h3 className="m-0 text-[16px] font-semibold">No data for {periodLabel(mode, range)}</h3>
          <p className="m-0 text-[13px] text-[#5B6474] max-w-[420px]">
            {isClient
              ? (isFpsScope ? "No hours were logged on FPS projects in this period. The FPS projects are listed below."
                : `No hours were logged on ${scopeLabel}'s projects in this period. Its active projects are listed below.`)
              : `No hours were logged on ${scopeLabel} in this period.`}
          </p>
          <div className="flex flex-wrap justify-center gap-2 mt-2">
            <button type="button" onClick={onPrevPeriod}
              className="h-10 px-4 rounded-[10px] border border-[#D5DAE3] bg-white text-[13px] font-medium cursor-pointer hover:bg-[#F8FAFC]">
              Previous {mode}
            </button>
            {mode !== "year" && (
              <button type="button" onClick={onYearView}
                className="h-10 px-4 rounded-[10px] border-0 bg-[#1D5FD1] text-white text-[13px] font-semibold cursor-pointer hover:bg-[#164AA6]">
                View whole year
              </button>
            )}
          </div>
        </Card>
      )}

      {hasData && (
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
        <Kpi label="Total hours" value={fmt(totals.h)} unit="h" sub="Click: all hours, per day"
          onClick={() => { setBill("all"); setProjFilter("all"); goTo(chartRef); }} />
        <Kpi label="Billable" swatch={BILL} value={fmt(totals.b)} unit="h" active={bill === "billable"} activeColor={BILL}
          onClick={() => { const on = bill !== "billable"; setBill(on ? "billable" : "all"); goTo(on ? (isClient ? projRef : taskRef) : chartRef); }}
          sub={hasFps ? `Normal ${fmt(split.normal.b)} h · FPS ${fmt(split.fps.b)} h` : totals.h ? `${fmt((totals.b / totals.h) * 100)}% of total` : null} />
        <Kpi label="Non-billable" swatch={NONBILL} value={fmt(totals.n)} unit="h" active={bill === "nonbillable"} activeColor={NONBILL}
          onClick={() => { const on = bill !== "nonbillable"; setBill(on ? "nonbillable" : "all"); goTo(on ? (isClient ? projRef : taskRef) : chartRef); }}
          sub={hasFps ? `Normal ${fmt(split.normal.n)} h · FPS ${fmt(split.fps.n)} h` : null} />
        {hasFps && (
          <Kpi label="FPS projects" swatch={FPS} value={fmt(split.fps.h)} unit="h" active={projFilter === "fps"} activeColor={FPS}
            onClick={() => { const on = projFilter !== "fps"; setProjFilter(on ? "fps" : "all"); if (on) goTo(projRef); }}
            sub={`${fpsProjects.filter((p) => p.hours > 0).length} of ${fpsProjects.length} with hours · ${fpsPeople} ${fpsPeople === 1 ? "person" : "people"} · click to filter`} />
        )}
        {isClient && <Kpi label="Projects" value={projects.filter((p) => p.hours > 0).length} unit={`of ${projects.length}`} sub="with hours / active" onClick={() => goTo(projRef)} />}
        <Kpi label="People" value={totals.logged} unit={`of ${allEmps.length}`} sub="logged hours / assigned" onClick={() => goTo(peopleRef)} />
        <Kpi label="Avg utilization" value={`${fmt(avgUtil)}%`} sub="people who logged hours" onClick={() => goTo(peopleRef)} />
      </div>
      )}

      {hasData && (bill !== "all" || projFilter !== "all") && (
        <div className="flex flex-wrap items-center gap-2 text-[13px] rounded-[10px] border border-[#BFD3F6] bg-[#EEF3FC] px-3 py-2">
          <span className="font-semibold text-[#164AA6]">Showing:</span>
          {bill !== "all" && <span className="flex items-center gap-1.5"><Swatch color={bill === "billable" ? BILL : NONBILL} />{billLabel} projects, tasks and people</span>}
          {projFilter !== "all" && <span className="flex items-center gap-1.5"><Swatch color={projFilter === "fps" ? FPS : BILL} />{projFilter === "fps" ? "FPS" : "Normal"} projects</span>}
          <span className="flex-1" />
          <button type="button" onClick={() => { setBill("all"); setProjFilter("all"); }}
            className="h-7 px-3 rounded-lg border border-[#BFD3F6] bg-white text-[12px] text-[#164AA6] cursor-pointer">Show all</button>
        </div>
      )}

      <div ref={chartRef} className="scroll-mt-2" />
      {hasData && mode !== "day" && (
        <Card className="p-5 flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="m-0 text-[15px] font-semibold">
              Hours per {mode === "year" ? "month" : "day"}
              {focusEmp != null && <span className="font-normal text-[#5B6474]"> · {allEmps.find((e) => e.employee_id === focusEmp)?.employee_name}</span>}
            </h3>
            <div className="flex items-center gap-4 text-[12px] text-[#3D4555]">
              <span className="flex items-center gap-1.5"><Swatch color={BILL} />Billable</span>
              <span className="flex items-center gap-1.5"><Swatch color={NONBILL} />Non-billable</span>
              {focusEmp != null && (
                <button type="button" onClick={() => setFocusEmp(null)} className="h-8 px-3 rounded-lg border border-[#D5DAE3] bg-white text-[12px] cursor-pointer">Show everyone</button>
              )}
            </div>
          </div>
          <HoursChart buckets={buckets} values={chartValues} />
        </Card>
      )}

      {/* Client view: projects of this client */}
      {isClient && <div ref={projRef} className="scroll-mt-2" />}
      {isClient && (
        <Card className="overflow-hidden">
          <div className="px-5 pt-4 pb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="m-0 text-[15px] font-semibold">{billLabel ? `${billLabel} ` : ""}{isFpsScope ? "FPS projects" : billLabel ? "projects" : "Projects"}</h3>
            <div className="flex flex-wrap items-center gap-3">
              {hasFps && (
                <div role="tablist" aria-label="Project type" className="flex bg-[#F1F3F7] rounded-[10px] p-1 gap-1">
                  {[["all", `All (${projects.length})`], ["normal", `Normal (${normalProjects.length})`], ["fps", `FPS (${fpsProjects.length})`]].map(([id, label]) => (
                    <button key={id} type="button" role="tab" aria-selected={projFilter === id} onClick={() => setProjFilter(id)}
                      className={`h-7 px-3 rounded-lg border-0 text-[12px] cursor-pointer ${projFilter === id ? "bg-white text-[#164AA6] font-semibold shadow-sm" : "bg-transparent text-[#3D4555]"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
              <span className="text-[12px] text-[#5B6474]">Click a project for its people</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px] min-w-[640px]">
              <thead>
                <tr className="text-left text-[12px] text-[#5B6474] border-b border-[#EEF0F4]">
                  <th className="font-medium py-2 pl-5 pr-2">Project</th>
                  <th className="font-medium py-2 px-2 text-right">Hours</th>
                  <th className="font-medium py-2 px-2 text-right">Billable</th>
                  <th className="font-medium py-2 px-2 text-right">Non-billable</th>
                  <th className="font-medium py-2 px-2 text-right">People</th>
                  <th className="font-medium py-2 px-2 pr-5 text-right">{isFpsScope ? "Share of FPS hours" : "Share of client hours"}</th>
                </tr>
              </thead>
              <tbody>
                {(hasFps
                  ? [["normal", "Normal projects", normalProjects, split.normal], ["fps", "FPS projects", fpsProjects, split.fps]].filter(([id]) => projFilter === "all" || projFilter === id)
                  : [["all", null, projects, null]]
                ).map(([gid, glabel, list0, tot]) => [gid, glabel, list0.filter(projBillOk), tot]).filter(([, , list]) => bill === "all" || list.length > 0).map(([gid, glabel, list, tot]) => (
                  <React.Fragment key={gid}>
                  {glabel && (
                    <tr className={gid === "fps" ? "bg-[#F7F3FE]" : "bg-[#F8F9FB]"}>
                      <td colSpan={6} className="py-2 pl-5 pr-5 text-[12px]">
                        <span className="flex flex-wrap items-center justify-between gap-2">
                          <span className="flex items-center gap-2 font-semibold text-[#0F172A]">
                            <Swatch color={gid === "fps" ? FPS : BILL} />{glabel} <span className="font-normal text-[#5B6474]">· {list.length}</span>
                          </span>
                          <span className="text-[#3D4555]">
                            <b>{fmt(tot.h)} h</b> · billable {fmt(tot.b)} h · non-billable {fmt(tot.n)} h
                          </span>
                        </span>
                      </td>
                    </tr>
                  )}
                  {list.length === 0 && (
                    <tr><td colSpan={6} className="py-2.5 pl-5 text-[12px] text-[#5B6474]">None.</td></tr>
                  )}
                  {list.map((p) => (
                  <tr key={p.project_id} onClick={() => onOpenProject(p.project_id)} className="border-b border-[#F1F3F7] cursor-pointer hover:bg-[#FAFBFC]">
                    <td className="py-2.5 pl-5 pr-2">
                      <span className="flex items-center gap-2">
                        <span className="flex flex-col min-w-0">
                          <span className="flex items-center gap-1.5 font-medium text-[#164AA6]">{p.project_name}{projIsFps(p) && !isFpsScope && <FpsChip />}</span>
                          {(p.chargeability || (isFpsScope && p.client_name)) && (
                            <span className="text-[11px] text-[#5B6474]">{[isFpsScope ? p.client_name : "", p.chargeability].filter(Boolean).join(" · ")}</span>
                          )}
                        </span>
                        <FiChevronRight aria-hidden="true" className="text-[#8A93A3] ml-auto" />
                      </span>
                    </td>
                    <td className="py-2.5 px-2 text-right font-semibold">
                      <span className="inline-flex flex-col items-end gap-1">
                        {fmt(p.hours)} h
                        <span className="w-16 h-1 bg-[#F1F3F7] rounded-full overflow-hidden"><span className="block h-1 rounded-full" style={{ width: `${(p.hours / maxProjHours) * 100}%`, background: BILL }} /></span>
                      </span>
                    </td>
                    <td className="py-2.5 px-2 text-right">{fmt(p.billable_hours)}</td>
                    <td className="py-2.5 px-2 text-right">{fmt(p.non_billable_hours)}</td>
                    <td className="py-2.5 px-2 text-right">{p.people_logged}/{p.people_assigned}</td>
                    <td className="py-2.5 px-2 pr-5 text-right">{projTotal ? `${fmt((p.hours / projTotal) * 100)}%` : "—"}</td>
                  </tr>
                  ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Billable / Non-billable tasks (after clicking those cards) */}
      <div ref={taskRef} className="scroll-mt-2" />
      {hasData && bill !== "all" && (
        <Card className="overflow-hidden">
          <div className="px-5 pt-4 pb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="m-0 text-[15px] font-semibold flex items-center gap-2">
              <Swatch color={bill === "billable" ? BILL : NONBILL} />{billLabel} tasks
              <span className="font-normal text-[#5B6474]">· {taskRows.length} · {fmt(taskRows.reduce((t, x) => t + x.hours, 0))} h</span>
            </h3>
            <span className="text-[12px] text-[#5B6474]">Click a task's project to open it</span>
          </div>
          {taskRows.length === 0 ? (
            <p className="px-5 pb-5 m-0 text-[13px] text-[#5B6474]">No {billLabel.toLowerCase()} hours in this period.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[13px] min-w-[720px]">
                <thead>
                  <tr className="text-left text-[12px] text-[#5B6474] border-b border-[#EEF0F4]">
                    <th className="font-medium py-2 pl-5 pr-2">Task</th>
                    <th className="font-medium py-2 px-2">Project</th>
                    <th className="font-medium py-2 px-2">Who worked</th>
                    <th className="font-medium py-2 px-2 pr-5 text-right">Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {taskRows.map((t) => (
                    <tr key={t.key} className="border-b border-[#F1F3F7] align-top">
                      <td className="py-2.5 pl-5 pr-2 font-medium">{t.item_name || "Untitled task"}</td>
                      <td className="py-2.5 px-2">
                        <button type="button" onClick={() => onOpenProject(t.project_id)}
                          className="p-0 border-0 bg-transparent text-left text-[13px] text-[#164AA6] cursor-pointer hover:underline">{t.project_name}</button>
                        {t.fps && !isFpsScope && <span className="ml-1.5"><FpsChip /></span>}
                        {isFpsScope && t.client_name && <span className="block text-[11px] text-[#5B6474]">{t.client_name}</span>}
                      </td>
                      <td className="py-2.5 px-2 text-[12px] text-[#3D4555]">
                        {t.people.sort((a, b) => b.hours - a.hours).map((x) => `${x.name} ${fmt(x.hours)} h`).join(" · ")}
                      </td>
                      <td className="py-2.5 px-2 pr-5 text-right font-semibold tabular-nums">{fmt(t.hours)} h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* People */}
      <div ref={peopleRef} className="scroll-mt-2" />
      {hasData && (
      <Card className="overflow-hidden">
        <div className="px-5 pt-4 pb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="m-0 text-[15px] font-semibold">
            People{hasFps && projFilter !== "all" && <span className="font-normal text-[#5B6474]"> · worked on {projFilter === "fps" ? "FPS" : "normal"} projects</span>}
            {bill !== "all" && <span className="font-normal text-[#5B6474]"> · with {billLabel.toLowerCase()} hours</span>}
          </h3>
          <span className="text-[12px] text-[#5B6474]">Click a person for {isClient ? "projects, " : ""}tasks and comment hours</span>
        </div>
        {emps.length === 0 ? (
          <p className="px-5 pb-5 m-0 text-[13px] text-[#5B6474]">{hasFps && projFilter !== "all" ? `No one logged hours on ${projFilter === "fps" ? "FPS" : "normal"} projects in this period.` : "No one is assigned or logged hours in this period."}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px] min-w-[920px]">
              <thead>
                <tr className="text-left text-[12px] text-[#5B6474] border-b border-[#EEF0F4]">
                  <th className="font-medium py-2 pl-5 pr-2">Employee</th>
                  <th className="font-medium py-2 px-2 text-right">Hours</th>
                  <th className="font-medium py-2 px-2 text-right">Billable</th>
                  <th className="font-medium py-2 px-2 text-right">Non-billable</th>
                  {hasFps && <th className="font-medium py-2 px-2 text-right" title="Hours on FPS projects (included in Hours)">FPS</th>}
                  <th className="font-medium py-2 px-2 text-right" title="Saturday / Sunday / leave-day hours and hours above 8 h on a working day">Extra</th>
                  <th className="font-medium py-2 px-2 text-right" title="Hours logged on company holidays">Holiday</th>
                  <th className="font-medium py-2 px-2 text-right">Days</th>
                  <th className="font-medium py-2 px-2 w-[200px]">Utilization</th>
                  <th className="font-medium py-2 px-2 pr-5 text-right">Share of their time</th>
                </tr>
              </thead>
              <tbody>
                {emps.map((e) => {
                  const open = expanded === e.employee_id;
                  const over = e.utilization_pct > 100;
                  return (
                    <React.Fragment key={e.employee_id}>
                      <tr onClick={() => { setExpanded(open ? null : e.employee_id); setFocusEmp(open ? null : e.employee_id); }}
                        aria-expanded={open}
                        className={`border-b border-[#F1F3F7] cursor-pointer ${open ? "bg-[#F4F6FA]" : "hover:bg-[#FAFBFC]"}`}>
                        <td className="py-2.5 pl-5 pr-2">
                          <span className="flex items-center gap-2">
                            <FiChevronDown aria-hidden="true" className={`shrink-0 text-[#8A93A3] transition-transform ${open ? "rotate-180" : ""}`} />
                            <span className="flex flex-col">
                              <span className="font-medium">{e.employee_name}</span>
                              <span className="text-[11px] text-[#5B6474]">
                                {e.role}{!e.assigned ? " · not assigned" : ""}{isClient && e.projects.length ? ` · ${e.projects.length} project${e.projects.length === 1 ? "" : "s"}` : ""}
                              </span>
                            </span>
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-right font-semibold">
                          <span className="inline-flex flex-col items-end gap-1">
                            {fmt(e.hours)} h
                            <span className="w-16 h-1 bg-[#F1F3F7] rounded-full overflow-hidden"><span className="block h-1 rounded-full" style={{ width: `${(e.hours / maxEmpHours) * 100}%`, background: BILL }} /></span>
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-right">{fmt(e.billable_hours)}</td>
                        <td className="py-2.5 px-2 text-right">{fmt(e.non_billable_hours)}</td>
                        {hasFps && <td className="py-2.5 px-2 text-right text-[#5B33A6]">{empFps(e) > 0 ? fmt(empFps(e)) : "—"}</td>}
                        <td className="py-2.5 px-2 text-right">{e.extra_hours > 0 ? fmt(e.extra_hours) : "—"}</td>
                        <td className="py-2.5 px-2 text-right">{e.holiday_hours > 0 ? fmt(e.holiday_hours) : "—"}</td>
                        <td className="py-2.5 px-2 text-right">{e.days_worked}</td>
                        <td className="py-2.5 px-2">
                          <span className="flex items-center gap-2">
                            <span className="flex-1 h-2 bg-[#F1F3F7] rounded-full overflow-hidden">
                              <span className="block h-2 rounded-full" style={{ width: `${Math.min(100, e.utilization_pct)}%`, background: over ? OVER : BILL }} />
                            </span>
                            <span className={`w-[52px] text-right ${over ? "text-[#C2410C] font-semibold" : ""}`}>{fmt(e.utilization_pct)}%</span>
                          </span>
                          {over && (
                            <span className="text-[11px] text-[#C2410C] flex items-center gap-1 mt-0.5"><FiAlertTriangle aria-hidden="true" />Over capacity</span>
                          )}
                        </td>
                        <td className="py-2.5 px-2 pr-5 text-right">
                          {fmt(e.share_pct)}%
                        </td>
                      </tr>
                      {open && (
                        <tr className="bg-[#F4F6FA] border-b border-[#EEF0F4]">
                          <td colSpan={hasFps ? 10 : 9} className="px-5 pb-4 pt-1">
                            <div className="flex flex-col gap-3 pl-6">
                              {e.projects.length === 0 ? (
                                <span className="text-[12px] text-[#5B6474]">No hours logged in this period.</span>
                              ) : (
                                e.projects.filter((p) => p.tasks.some(taskBillOk)).map((p) => (
                                  <div key={p.project_id} className="bg-white border border-[#E6E9EF] rounded-[10px] p-3 flex flex-col gap-2.5">
                                    {isClient && (
                                      <div className="flex items-center justify-between gap-3 border-b border-[#EEF0F4] pb-2">
                                        <button type="button" onClick={(ev) => { ev.stopPropagation(); onOpenProject(p.project_id); }}
                                          className="p-0 border-0 bg-transparent text-left text-[13px] font-semibold text-[#164AA6] cursor-pointer hover:underline">
                                          {p.project_name}
                                        </button>
                                        {projIsFps(p) && !isFpsScope && <FpsChip />}
                                        {isFpsScope && p.client_name && <span className="text-[11px] text-[#5B6474]">{p.client_name}</span>}
                                        <span className="text-[13px] font-semibold">{fmt(p.hours)} h</span>
                                      </div>
                                    )}
                                    <ul className="m-0 p-0 list-none flex flex-col gap-3">
                                      {p.tasks.filter(taskBillOk).map((t) => <TaskBlock key={t.item_id} t={t} />)}
                                    </ul>
                                  </div>
                                ))
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      )}

    </>
  );
}

// ── Page ─────────────────────────────────────────────────────────────
export const Utilization = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = MODES.some((m) => m.id === params.get("mode")) ? params.get("mode") : "month";
  const anchor = parseISO(params.get("date")) || new Date();
  const clientId = Number(params.get("client")) || null;
  const projectId = Number(params.get("project")) || null;
  const fpsView = params.get("fps") === "1" && !clientId;   // FPS projects of every client
  const bill = ["billable", "nonbillable"].includes(params.get("bill")) ? params.get("bill") : "all";

  const range = useMemo(() => periodRange(mode, anchor), [mode, toISO(anchor)]); // eslint-disable-line react-hooks/exhaustive-deps
  const rangeKey = `${toISO(range.from)}_${toISO(range.to)}`;

  const [clients, setClients] = useState([]);
  const [listState, setListState] = useState("loading");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [detailState, setDetailState] = useState("idle");
  const [detailError, setDetailError] = useState("");

  // Large screens: the client list and the detail scroll separately, so the list never moves.
  // Height = from the top of the two panes to the bottom of the window (measured, so any header height works).
  const panesRef = useRef(null);
  const [paneH, setPaneH] = useState(null);
  useLayoutEffect(() => {
    const measure = () => {
      const el = panesRef.current;
      if (!el || window.innerWidth < 1024) { setPaneH(null); return; }
      const top = el.getBoundingClientRect().top + window.scrollY;
      setPaneH(Math.max(420, window.innerHeight - top - 16));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v == null || v === "" ? next.delete(k) : next.set(k, v)));
    setParams(next, { replace: true });
  };

  useEffect(() => {
    let cancelled = false;
    setListState("loading");
    fetchActiveClients(range)
      .then((list) => { if (!cancelled) { setClients(list); setListState("ready"); } })
      .catch((err) => { console.error("Clients load failed:", err); if (!cancelled) setListState("error"); });
    return () => { cancelled = true; };
  }, [rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Project wins over client when both are in the URL
  useEffect(() => {
    if (!projectId && !clientId && !fpsView) { setData(null); setDetailState("idle"); return undefined; }
    let cancelled = false;
    setDetailState("loading");
    (projectId ? fetchProjectUtilization(projectId, range) : fpsView ? fetchFpsUtilization(range) : fetchClientUtilization(clientId, range))
      .then((d) => { if (!cancelled) { setData(d); setDetailState("ready"); } })
      .catch((err) => {
        console.error("Utilization load failed:", err);
        if (!cancelled) { setDetailError(err.message || "Couldn't load this view."); setDetailState("error"); }
      });
    return () => { cancelled = true; };
  }, [clientId, projectId, fpsView, rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? clients.filter((c) => (c.client_name || "").toLowerCase().includes(q)) : clients;
  }, [clients, search]);
  const maxClientHours = Math.max(1, ...clients.map((c) => c.total_hours || 0));
  const selectedClientId = fpsView ? null : projectId ? data?.scope?.client_id ?? clientId : clientId;
  const fpsTotals = clients.reduce((t, c) => ({ h: t.h + (Number(c.fps_hours) || 0), n: t.n + (Number(c.fps_projects_count) || 0) }), { h: 0, n: 0 });
  const clientName = clients.find((c) => c.client_id === selectedClientId)?.client_name || data?.scope?.client_name;

  return (
    <div className="pt-8 px-[clamp(12px,2.5vw,40px)] pb-6 bg-[#F4F6FA] min-h-screen text-[#0F172A]" style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}>
      {/* Full width on big screens (up to 1680px), wraps on laptops — same as the dashboard's Admin view */}
      <div className="max-w-[1680px] mx-auto flex flex-col gap-5">

        <header className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-1">
            {params.get("from") === "dashboard" && (
              <button type="button" onClick={() => navigate("/dashboard?view=admin")}
                className="self-start flex items-center gap-1 p-0 mb-1 border-0 bg-transparent text-[13px] text-[#1D5FD1] font-medium cursor-pointer hover:underline">
                <FiChevronLeft aria-hidden="true" /> Back to dashboard
              </button>
            )}
            <h1 className="m-0 text-[26px] font-semibold tracking-[-0.01em]">Utilization</h1>
            <p className="m-0 text-[13px] text-[#5B6474]">Hours by client, project and person</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div role="tablist" aria-label="Period" className="flex bg-white border border-[#E6E9EF] rounded-[10px] p-1">
              {MODES.map((m) => (
                <button key={m.id} type="button" role="tab" aria-selected={mode === m.id} onClick={() => update({ mode: m.id })}
                  className={`min-h-[36px] px-3.5 rounded-lg border-0 text-[13px] cursor-pointer ${mode === m.id ? "bg-[#E3ECFB] text-[#164AA6] font-semibold" : "bg-transparent text-[#3D4555] hover:bg-[#F4F6FA]"}`}>
                  {m.label}
                </button>
              ))}
            </div>
            <div className="flex items-center bg-white border border-[#E6E9EF] rounded-[10px]">
              <button type="button" aria-label="Previous period" onClick={() => update({ date: toISO(shiftAnchor(mode, anchor, -1)) })}
                className="w-10 h-11 flex items-center justify-center border-0 bg-transparent cursor-pointer text-[#3D4555] hover:text-[#1D5FD1]"><FiChevronLeft /></button>
              <span className="min-w-[150px] text-center text-[13px] font-medium">{periodLabel(mode, range)}</span>
              <button type="button" aria-label="Next period" onClick={() => update({ date: toISO(shiftAnchor(mode, anchor, 1)) })}
                className="w-10 h-11 flex items-center justify-center border-0 bg-transparent cursor-pointer text-[#3D4555] hover:text-[#1D5FD1]"><FiChevronRight /></button>
            </div>
            <input type="date" aria-label="Jump to date" value={toISO(anchor)} onChange={(e) => e.target.value && update({ date: e.target.value })}
              className="h-11 px-3 rounded-[10px] border border-[#E6E9EF] bg-white text-[13px] text-[#0F172A]" />
            <button type="button" onClick={() => update({ date: null })}
              className="h-11 px-4 rounded-[10px] border border-[#D5DAE3] bg-white text-[13px] font-medium cursor-pointer hover:bg-[#F8FAFC]">Today</button>
          </div>
        </header>

        <div ref={panesRef} className="grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)] items-start" style={paneH ? { height: paneH } : undefined}>

          {/* Clients */}
          <Card className="p-3 flex flex-col gap-2 lg:h-full lg:min-h-0">
            <div className="flex items-center justify-between px-1 pt-1">
              <h2 className="m-0 text-[15px] font-semibold">Clients</h2>
              <span className="text-[12px] text-[#5B6474]">{listState === "ready" ? clients.length : ""}</span>
            </div>
            <label className="relative block">
              <FiSearch aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A93A3]" />
              <input type="search" placeholder="Search client" value={search} onChange={(e) => setSearch(e.target.value)}
                className="w-full h-10 pl-9 pr-3 rounded-[10px] border border-[#E6E9EF] text-[13px] box-border" />
            </label>
            <div className={`flex flex-col gap-1 overflow-y-auto pr-1 ${paneH ? "flex-1 min-h-0" : "max-h-[60vh]"}`}>
              {listState === "loading" && Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} h={58} />)}
              {listState === "error" && <p className="m-2 text-[13px] text-[#9A3412]">Couldn't load clients. Try again.</p>}
              {listState === "ready" && filtered.length === 0 && <p className="m-2 text-[13px] text-[#5B6474]">{clients.length ? "No match." : "No clients with active projects."}</p>}
              {listState === "ready" && fpsTotals.n > 0 && (
                <button type="button" onClick={() => update({ fps: "1", client: null, project: null })}
                  aria-current={fpsView && !projectId ? "true" : undefined}
                  className={`text-left w-full rounded-[10px] px-3 py-2.5 border cursor-pointer flex flex-col gap-1 ${fpsView ? "bg-[#F1ECFB] border-[#C9B8F0]" : "bg-[#FAF8FE] border-[#EDE6FB] hover:bg-[#F1ECFB]"}`}>
                  <span className="flex justify-between gap-2">
                    <span className={`text-[13px] flex items-center gap-1.5 ${fpsView ? "font-semibold text-[#5B33A6]" : "font-medium"}`}><FiLayers aria-hidden="true" />FPS projects · all clients</span>
                    <span className="text-[13px] font-semibold shrink-0">{fmt(fpsTotals.h)} h</span>
                  </span>
                  <span className="text-[11px] text-[#5B6474]">{fpsTotals.n} FPS project{fpsTotals.n === 1 ? "" : "s"} · who worked and how many hours</span>
                </button>
              )}
              {listState === "ready" && filtered.map((c) => {
                const active = c.client_id === selectedClientId;
                return (
                  <button key={c.client_id} type="button" onClick={() => update({ client: c.client_id, project: null, fps: null })}
                    aria-current={active ? "true" : undefined}
                    className={`text-left w-full rounded-[10px] px-3 py-2.5 border cursor-pointer flex flex-col gap-1 ${active ? "bg-[#E3ECFB] border-[#BFD3F6]" : "bg-white border-transparent hover:bg-[#F4F6FA]"}`}>
                    <span className="flex justify-between gap-2">
                      <span className={`text-[13px] truncate ${active ? "font-semibold text-[#164AA6]" : "font-medium"}`}>{c.client_name}</span>
                      <span className="text-[13px] font-semibold shrink-0">{fmt(c.total_hours)} h</span>
                    </span>
                    <span className="flex justify-between gap-2 text-[11px] text-[#5B6474]">
                      <span>{c.projects_count} project{c.projects_count === 1 ? "" : "s"}{c.fps_projects_count > 0 ? ` · FPS ${fmt(c.fps_hours)} h` : ""}</span>
                      <span className="flex items-center gap-1"><FiUsers aria-hidden="true" />{c.people_logged}</span>
                    </span>
                    <span className="h-1 bg-[#F1F3F7] rounded-full overflow-hidden">
                      <span className="block h-1 rounded-full" style={{ width: `${(c.total_hours / maxClientHours) * 100}%`, background: BILL }} />
                    </span>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Detail */}
          <div className={`flex flex-col gap-4 min-w-0 [&>*]:shrink-0 ${paneH ? "h-full overflow-y-auto pr-1 pb-4" : ""}`}>
            {(projectId || clientId || fpsView) && (
              <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[13px] text-[#5B6474]">
                <button type="button" onClick={() => update({ client: null, project: null, fps: null })} className="p-0 border-0 bg-transparent text-[#1D5FD1] cursor-pointer hover:underline">All clients</button>
                {fpsView && (
                  <>
                    <FiChevronRight aria-hidden="true" />
                    {projectId ? (
                      <button type="button" onClick={() => update({ project: null })} className="p-0 border-0 bg-transparent text-[#1D5FD1] cursor-pointer hover:underline">FPS projects</button>
                    ) : <span className="text-[#0F172A] font-medium">FPS projects</span>}
                  </>
                )}
                {!fpsView && clientName && (
                  <>
                    <FiChevronRight aria-hidden="true" />
                    {projectId ? (
                      <button type="button" onClick={() => update({ client: selectedClientId, project: null })} className="p-0 border-0 bg-transparent text-[#1D5FD1] cursor-pointer hover:underline">{clientName}</button>
                    ) : <span className="text-[#0F172A] font-medium">{clientName}</span>}
                  </>
                )}
                {projectId && data?.scope?.project_name && (<><FiChevronRight aria-hidden="true" /><span className="text-[#0F172A] font-medium">{data.scope.project_name}</span></>)}
              </nav>
            )}

            {detailState === "idle" && <Card className="p-10 text-center text-[14px] text-[#5B6474]">Select a client, or FPS projects, to see the projects and each person's hours.</Card>}
            {detailState === "error" && <Card className="p-6 text-[14px] text-[#9A3412] flex items-center gap-2"><FiAlertTriangle aria-hidden="true" />{detailError}</Card>}
            {detailState === "loading" && (
              <>
                <Skeleton h={56} />
                <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} h={90} />)}</div>
                <Skeleton h={220} />
                <Skeleton h={260} />
              </>
            )}
            {detailState === "ready" && data && (
              <Detail key={`${clientId}-${projectId}-${fpsView}-${rangeKey}`} data={data} mode={mode} range={range}
                bill={bill} setBill={(b) => update({ bill: b === "all" ? null : b })}
                onOpenProject={(id) => update(fpsView ? { project: id } : { project: id, client: selectedClientId })}
                onPrevPeriod={() => update({ date: toISO(shiftAnchor(mode, anchor, -1)) })}
                onYearView={() => update({ mode: "year" })} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};